import { Component, Injector, OnInit } from '@angular/core';
import { Database, get, ref } from '@angular/fire/database';
import { BaseComponent } from 'src/app/shared/baseComponent';
import { AuthService } from 'src/app/services/auth.service';
import { EventType, EventTypeClassMap, Event } from './eventClasses';

@Component({
  selector: 'app-eventclasses',
  templateUrl: './eventclasses.component.html',
  styleUrls: ['./eventclasses.component.css']
})
export class EventclassesComponent extends BaseComponent implements OnInit {
  constructor(injector: Injector, private authService: AuthService, private database: Database) {
    super(injector)
  }

  isAuthenticatedWithFirebase = false
  loading = false
  // flattened list of all events across exercises
  events: any[] = []
  // events filtered by selected exercise
  exerciseEvents: any[] = []
  // hydrated + validation metadata for display
  exerciseEventsHydrated: { raw: any; hydrated: Event; isValid: boolean; missing: string[]; extra: string[] }[] = []
  // list of unique exercises with counts and latest timestamp
  exercises: { id: string; count: number; latestTimestamp: number; latestDatetime: string }[] = []
  selectedExerciseId: string | null = null
  eventsSizeKB = 0

  async ngOnInit(): Promise<void> {
    // attempt silent auth (re-uses existing session) else show sign-in prompt in UI
    this.isAuthenticatedWithFirebase = await this.authService.initializeAuth(() => this.onSuccessfulLogin())
    if (this.isAuthenticatedWithFirebase) {
      await this.onSuccessfulLogin()
    }
  }

  async signInWithGoogle() {
    this.isAuthenticatedWithFirebase = await this.authService.signInWithGoogle()
    if (this.isAuthenticatedWithFirebase) await this.onSuccessfulLogin()
  }

  private async onSuccessfulLogin() {
    await this.loadEvents()
  }

  async loadEvents() {
    this.loading = true
    try {
      const eventsSnap = await get(ref(this.database, 'events'))
      if (!eventsSnap.exists()) {
        this.events = []
        this.exercises = []
        this.exerciseEvents = []
        return
      }
      const raw = eventsSnap.val() || {}
      const json = JSON.stringify(raw)
      this.eventsSizeKB = new Blob([json]).size / 1024

      const flattened: any[] = []
      Object.entries(raw).forEach(([exerciseid, eventsObj]: [string, any]) => {
        if (!eventsObj) return
        Object.entries(eventsObj).forEach(([eventId, eventData]: [string, any]) => {
          if (!eventData) return
            // attach metadata
            const evt = {
              id: eventId,
              exerciseid,
              ...eventData
            }
            evt.datetime = this.convertTimestamp(evt.timestamp)
            flattened.push(evt)
        })
      })

      // sort newest first
      flattened.sort((a, b) => b.timestamp - a.timestamp)
      this.events = flattened
      // aggregate counts and latest timestamp by exercise
      const stats: Record<string, { count: number; latest: number }> = {}
      flattened.forEach(e => {
        const cur = stats[e.exerciseid] || { count: 0, latest: 0 }
        cur.count++
        if (typeof e.timestamp === 'number' && e.timestamp > cur.latest) cur.latest = e.timestamp
        stats[e.exerciseid] = cur
      })
      this.exercises = Object.entries(stats)
        .map(([id, v]) => ({ id, count: v.count, latestTimestamp: v.latest, latestDatetime: this.convertTimestamp(v.latest) }))
        .sort((a, b) => b.latestTimestamp - a.latestTimestamp)

      // maintain selection if still available
  if (this.selectedExerciseId && stats[this.selectedExerciseId]) {
        this.selectExercise(this.selectedExerciseId)
      } else {
        this.selectedExerciseId = null
        this.exerciseEvents = []
      }
    } catch (err) {
      console.error('Error loading events', err)
      this.toast.danger('Failed to load events')
    } finally {
      this.loading = false
    }
  }

  selectExercise(exerciseid: string) {
    this.selectedExerciseId = exerciseid
  this.exerciseEvents = this.events.filter(e => e.exerciseid === exerciseid)
  this.hydrateAndValidate()
  }

  clearSelection() {
    this.selectedExerciseId = null
    this.exerciseEvents = []
    this.exerciseEventsHydrated = []
  }

  private hydrateAndValidate() {
    this.exerciseEventsHydrated = this.exerciseEvents.map(ev => {
      const type = ev.type as EventType
      const Ctor = (type && (EventTypeClassMap as any)[type]) ? (EventTypeClassMap as any)[type] as new () => Event : Event
      const hydrated = Object.assign(new Ctor(), ev)
      // Collect property sets
      const classProps = new Set(Object.keys(new Ctor()))
      const eventKeys = Object.keys(ev)
      const missing: string[] = []
      classProps.forEach(p => { if (!(p in ev)) missing.push(p) })
      const extra = eventKeys.filter(k => !classProps.has(k) && !['id','datetime','exerciseid'].includes(k))
      const isValid = missing.length === 0 && extra.length === 0
      return { raw: ev, hydrated, isValid, missing, extra }
    })
  }
}
